
import React, { useState, useEffect } from "react";
import { useAdmin } from "@/contexts/admin-context";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import FileValidationStatus from "./FileValidationStatus";
import { ValidationStage } from "@/services/agent/agent-validation-service";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Loader2, Shield, RefreshCw, FileSearch } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface ValidationLog {
  id: string;
  user_id: string;
  file_path: string;
  validation_stage: ValidationStage;
  passed: boolean;
  message: string;
  details: Record<string, any>;
  created_at: string;
  user_name?: string;
}

interface Agent {
  id: string;
  name: string;
}

const ValidationLogsViewer: React.FC = () => {
  const { isAdmin } = useAdmin();
  const [logs, setLogs] = useState<ValidationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Add state for agents to map agent IDs to names
  const [agents, setAgents] = useState<Record<string, string>>({});

  const fetchValidationLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all agent executions as validation logs
      const { data: executionsData, error: executionsError } = await supabase
        .from('agent_executions')
        .select(`
          id,
          agent_id,
          user_id,
          status,
          error,
          created_at
        `)
        .order('created_at', { ascending: false })
        .range((page - 1) * itemsPerPage, page * itemsPerPage - 1);

      if (executionsError) {
        throw executionsError;
      }

      // Get total count for pagination
      const { count } = await supabase
        .from('agent_executions')
        .select('*', { count: 'exact', head: true });

      if (count !== null) {
        setTotalPages(Math.ceil(count / itemsPerPage));
      }

      // Fetch the agent names
      const agentIds = [...new Set(executionsData.map((log: any) => log.agent_id))];
      let agentNames: Record<string, string> = {};
      
      if (agentIds.length > 0) {
        const { data: agentData, error: agentError } = await supabase
          .from('agents')
          .select('id, name')
          .in('id', agentIds);

        if (!agentError && agentData) {
          agentData.forEach((agent: Agent) => {
            agentNames[agent.id] = agent.name;
          });
        }
      }
      setAgents(agentNames);

      // Fetch user names
      const userIds = [...new Set(executionsData.map((log: any) => log.user_id))];
      let userNames: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: userData, error: userError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', userIds);

        if (!userError && userData) {
          userData.forEach((user: any) => {
            userNames[user.id] = user.name;
          });
        }
      }

      // Format the data to match the ValidationLog interface
      const formattedLogs: ValidationLog[] = executionsData.map((execution: any) => {
        // Map execution status to validation stage
        let validationStage: ValidationStage = ValidationStage.RECEIVED;
        let passed = false;

        if (execution.status === 'completed') {
          validationStage = ValidationStage.APPROVED;
          passed = true;
        } else if (execution.status === 'failed' && execution.error?.includes('virus')) {
          validationStage = ValidationStage.VIRUS_SCAN;
          passed = false;
        } else if (execution.status === 'failed' && execution.error?.includes('structure')) {
          validationStage = ValidationStage.STRUCTURE_VALIDATION;
          passed = false;
        } else if (execution.status === 'failed' && execution.error?.includes('size')) {
          validationStage = ValidationStage.EXTENSION_SIZE_CHECK;
          passed = false;
        } else if (execution.status === 'failed') {
          validationStage = ValidationStage.REJECTED;
          passed = false;
        } else if (execution.status === 'processing') {
          validationStage = ValidationStage.RECEIVED;
          passed = true;
        }

        const agentName = agentNames[execution.agent_id] || 'Unknown Agent';
        const filePath = `${agentName}.js`;

        return {
          id: execution.id,
          user_id: execution.user_id,
          file_path: filePath,
          validation_stage: validationStage,
          passed: passed,
          message: execution.error || 'File processed successfully',
          details: {},
          created_at: execution.created_at,
          user_name: userNames[execution.user_id] || 'Unknown User'
        };
      });

      setLogs(formattedLogs);
    } catch (err) {
      console.error('Error fetching validation logs:', err);
      setError('Failed to load validation logs. Please try again.');
      toast({
        title: "Error",
        description: "Failed to load validation logs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchValidationLogs();
    }
  }, [isAdmin, page]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const truncateFilePath = (path: string) => {
    const maxLength = 30;
    return path.length > maxLength ? `...${path.slice(-maxLength)}` : path;
  };

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
        </CardHeader>
        <CardContent>
          <p>You need administrator privileges to view validation logs.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center">
          <FileSearch className="mr-2 h-5 w-5" />
          <CardTitle>Agent File Validation Logs</CardTitle>
        </div>
        <Button variant="outline" onClick={() => fetchValidationLogs()} className="flex items-center">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="bg-destructive/10 text-destructive p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        No validation logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(log.created_at)}
                        </TableCell>
                        <TableCell>{log.user_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={log.file_path}>
                          {truncateFilePath(log.file_path)}
                        </TableCell>
                        <TableCell>
                          <FileValidationStatus 
                            stage={log.validation_stage} 
                            passed={log.passed} 
                          />
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate" title={log.message}>
                          {log.message}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <Pagination className="mt-4">
                <PaginationContent>
                  <PaginationItem>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                  </PaginationItem>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - page) < 2 || p === 1 || p === totalPages)
                    .map((p, i, arr) => {
                      // Add ellipsis where there are gaps
                      if (i > 0 && p > arr[i - 1] + 1) {
                        return (
                          <React.Fragment key={`ellipsis-${p}`}>
                            <PaginationItem>
                              <div className="px-3 py-2">...</div>
                            </PaginationItem>
                            <PaginationItem>
                              <Button
                                variant={page === p ? "default" : "outline"}
                                size="sm"
                                onClick={() => setPage(p)}
                              >
                                {p}
                              </Button>
                            </PaginationItem>
                          </React.Fragment>
                        );
                      }
                      return (
                        <PaginationItem key={p}>
                          <Button
                            variant={page === p ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </Button>
                        </PaginationItem>
                      );
                    })}
                  
                  <PaginationItem>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ValidationLogsViewer;
